import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Portal } from '../Portal/Portal';
import { ValidationError } from './validation';
import closeIcon from '../../assets/icons/close-icon.svg';
import './ProfileEditModal.scss';

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  title: string;
  children: ReactNode;
  isSubmitting?: boolean;
  validate?: (data: Record<string, any>) => ValidationError[];
}

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  children,
  validate,
  isSubmitting = false,
}) => {
  const { t } = useTranslation('profile');
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    if (validate) {
      const errors = validate(data);
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
    }

    setValidationErrors([]);
    try {
      await onSubmit(data);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Portal>
      <div className="profile-edit-modal">
        <div className="profile-edit-modal__overlay" onClick={onClose} />
        <div className="profile-edit-modal__content">
          <div className="profile-edit-modal__header">
            <h2>{title}</h2>
            <button className="profile-edit-modal__close" onClick={onClose} aria-label={t('actions.close')}>
              <img src={closeIcon} alt={t('actions.close')} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="profile-edit-modal__form">
            <div className="profile-edit-modal__body">
              {children}
            </div>
            {validationErrors.length > 0 && (
              <div className="profile-edit-modal__errors">
                {validationErrors.map((error, index) => (
                  <div key={index} className="profile-edit-modal__error">
                    {error.message}
                  </div>
                ))}
              </div>
            )}
            <div className="profile-edit-modal__footer">
              <button 
                type="button" 
                className="profile-edit-modal__button profile-edit-modal__button--cancel" 
                onClick={onClose}
              >
                {t('sections.actions.cancel')}
              </button>
              <button 
                type="submit" 
                className="profile-edit-modal__button profile-edit-modal__button--save"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('sections.actions.saving') : t('sections.actions.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
};
